import cx from "classnames";
import { flow, noop } from "lodash";
import PropTypes from "prop-types";
import * as React from "react";

import { parseXml } from "../utils/xml";

const SVG_OPEN_TAG_PATTERN = /<svg+[^>]*>/;
const SVG_CLOSE_TAG_PATTERN = /<\/svg>/;
const BASE_64_TAG_PATTERN = /data:image\/svg\+xml;base64,/;

class SvgIcon extends React.PureComponent {
  static propTypes = {
    className: PropTypes.string,
    blockName: PropTypes.string,
    src: PropTypes.string,
    onLoad: PropTypes.func,
    // raw loaded svg by 'raw-loader'
    svgRaw: (props, propName, componentName) => {
      if (!props.src && !props.svgRaw) {
        return new Error(
          `Warning: One of props 'src' or 'svgRaw' was not specified in '${componentName}'.`
        );
      } else if (props.svgRaw && typeof props.svgRaw !== "string") {
        return new Error(
          `Warning: Invalid prop 'svgRaw', expected 'string' in '${componentName}'.`
        );
      }

      return undefined;
    },
    /**
     * Add .nx-svg-icon--disabled and ${className}--disabled
     */
    disabled: PropTypes.bool,
    /**
     * Add .nx-svg-icon--active and ${className}--active
     */
    isActive: PropTypes.bool,
    /**
     * This is the svg color when the mouse hovers over the svg.
     */
    color: PropTypes.string,
    /**
     * Set fill for svg when mouse hovered
     */
    hoverColor: PropTypes.string,
    /**
     * Override the inline-styles of the root element.
     */
    style: PropTypes.object,
    /** Handlers */
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onError: PropTypes.func,
  };

  static defaultProps = {
    blockName: "nx-svg-icon",
    disabled: false,
    isActive: false,
    style: {},
    onMouseEnter: noop,
    onMouseLeave: noop,
    onError: noop,
  };

  xhr = null;

  mount = false; // to prevent setSvg method from setting the state when component was unmounted

  state = {
    hovered: false,
    svgTag: {},
    svgContentRaw: null,
  };

  componentDidMount() {
    const { svgRaw, src } = this.props;

    this.mount = true;

    if (svgRaw) {
      this.setSvg(svgRaw);
    } else if (src) {
      this.loadSvgRaw(src);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { src, svgRaw } = this.props;

    if (svgRaw !== nextProps.svgRaw) {
      this.setSvg(nextProps.svgRaw);
    } else if (src !== nextProps.src) {
      this.loadSvgRaw(nextProps.src);
    }
  }

  componentWillUnmount() {
    this.mount = false;

    if (this.xhr) {
      this.xhr.abort();
    }
  }

  loadSvgRaw = (src) => {
    const base64Matches = src.match(BASE_64_TAG_PATTERN);

    if (base64Matches) {
      const decodedSrc = atob(src.replace(base64Matches[0], ""));

      this.onLoadSvg(decodedSrc);
    } else {
      this.xhr = new XMLHttpRequest();
      this.xhr.open("GET", src);

      this.xhr.onload = () => {
        if (this.xhr.status >= 200 && this.xhr.status < 300) {
          this.onLoadSvg(this.xhr.responseText);
        } else {
          this.props.onError(`Could not donwnload SvgIcon from ${src}`);
        }
      };
      this.xhr.send();
    }
  };

  onLoadSvg = (svgRaw) => {
    const { onLoad } = this.props;

    this.setSvg(svgRaw);
    if (onLoad) onLoad();
  };

  setSvg = (svgRaw) => {
    this.setSvgTag(svgRaw)
      .then((svgTag) => {
        const svgContentRaw = flow([this.validateSvgFillColor, this.cutSvgTag])(
          svgRaw
        );

        if (this.mount) {
          this.setState({ svgTag, svgContentRaw });
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  };

  setSvgTag = (svgRaw) => {
    return new Promise((resolve, reject) => {
      const svgMatches = svgRaw.match(SVG_OPEN_TAG_PATTERN);

      if (svgMatches) {
        const openTag = svgMatches[0];

        parseXml(`${openTag}</svg>`, (error, result) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      }
    });
  };

  validateSvgFillColor = (svgRaw) => {
    const { color } = this.props;

    return color ? svgRaw.replace(/fill=[""'].+?[""']/g, "") : svgRaw;
  };

  cutSvgTag = (svgRaw) => {
    const pattern = new RegExp(
      `${SVG_OPEN_TAG_PATTERN.source}|${SVG_CLOSE_TAG_PATTERN.source}`,
      "g"
    );

    return svgRaw.replace(pattern, "");
  };

  handleMouseEnter = (event) => {
    if (!this.props.disabled) {
      this.setState({ hovered: true });
      this.props.onMouseEnter(event);
    }
  };

  handleMouseLeave = (event) => {
    if (!this.props.disabled) {
      this.setState({ hovered: false });
      this.props.onMouseLeave(event);
    }
  };

  render() {
    const {
      style,
      disabled,
      className,
      isActive,
      color,
      hoverColor,
      src, // eslint-disable-line no-unused-vars
      blockName,
      ...otherProps
    } = this.props;
    const { svgContentRaw, hovered, svgTag } = this.state;

    return (
      <svg
        {...svgTag.attributes}
        className={cx(blockName, className, {
          [`${blockName}--disabled`]: disabled,
          [`${blockName}--active`]: isActive,
          [`${blockName}--hovered`]: hovered,
          [`${className}--disabled`]: className && disabled,
          [`${className}--active`]: className && isActive,
          [`${className}--hovered`]: className && hovered,
        })}
        style={{
          fill: (!disabled && (isActive || hovered) && hoverColor) || color,
          ...style,
        }}
        {...otherProps}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: svgContentRaw }}
      />
    );
  }
}

export default SvgIcon;
